/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
/* eslint-disable no-confusing-arrow */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Col,
  Row,
  Container,
  Button,
  Modal,
  Form,
  ModalHeader,
  ModalBody,
  FormGroup,
  Input,
  FormFeedback,
  ModalFooter,
  Label,
} from 'reactstrap';

import { yupResolver } from '@hookform/resolvers/yup';

import AlertModal from '../../components/AlertModal';
import CustomSelect from '../../components/CustomSelect';
import CustomTable from '../../components/CustomTable';
import MoneyMaskedInput from '../../components/MoneyMaskedInput';
import useApp from '../../hooks/useApp';
import api from '../../services/api';
import { schema } from './validationSchema';

/** Converte para maiúsculas (pt-BR) ao digitar ou colar. */
function toUpperCasePtBr(raw) {
  if (raw == null) return '';
  return raw.toLocaleUpperCase('pt-BR');
}

function ActionsCell({ row, onEdit, onDelete, onClonar }) {
  return (
    <div className="d-flex align-items-center flex-wrap">
      <Button color="warning" size="sm" className="mr-2 mb-1" onClick={() => onEdit(row)}>
        <i className="fas fa-pen" />
      </Button>
      <Button color="danger" size="sm" className="mr-2 mb-1" onClick={() => onDelete(row)}>
        <i className="fas fa-trash" />
      </Button>
      <Button color="info" size="sm" className="mb-1" onClick={() => onClonar(row)}>
        <i className="fas fa-copy mr-1" />
        Clonar Reembolso
      </Button>
    </div>
  );
}

function Reembolsos() {
  const alertModalRef = useRef();
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [reembolsos, setReembolsos] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [reloadReembolsos, setReloadReembolsos] = useState(0);
  const [loadingReembolsos, setLoadingReembolsos] = useState(false);
  const [totalDocs, setTotalDocs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingComprovante, setEditingComprovante] = useState(null);
  const [showFileInput, setShowFileInput] = useState(true);
  const { setLoading, notificationRef } = useApp();
  const { control, clearErrors, handleSubmit, setValue, setError, watch, formState: { errors } } = useForm({ resolver: yupResolver(schema) });
  const responsavelTipo = watch('responsavelTipo');
  const formaPagamento = watch('formaPagamento');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingReembolsos(true);
        const { data } = await api.get('/profissionalReembolsos', { params: { page, limit } });
        if (cancelled) return;
        const docs = data?.docs ?? [];
        if (docs.length === 0 && page > 1) {
          setPage((p) => p - 1);
          return;
        }
        setReembolsos(docs);
        setTotalDocs(data?.totalDocs ?? 0);
        setTotalPages(data?.totalPages ?? 0);
        setHasPrevPage(!!data?.hasPrevPage);
        setHasNextPage(!!data?.hasNextPage);
      } catch (error) {
        if (!cancelled) {
          console.log('🚀 ~ file: reembolsos index.js ~ error:', error);
        }
      } finally {
        if (!cancelled) {
          setLoadingReembolsos(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, limit, reloadReembolsos]);

  useEffect(() => {
    // Só limpa o campo cliente se não for paciente, nic_sp ou nic_go
    // (nic_sp e nic_go são preenchidos automaticamente no outro useEffect)
    if (responsavelTipo && responsavelTipo !== 'paciente' && responsavelTipo !== 'nic_sp' && responsavelTipo !== 'nic_go') {
      setValue('cliente', '');
    }
  }, [responsavelTipo, setValue]);

  useEffect(() => {
    if (formaPagamento === 'reembolso') {
      setValue('ultimosNumerosCartao', '');
    }
  }, [formaPagamento, setValue]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingId(null);
    setEditingComprovante(null);
    setShowFileInput(true);
    setValue('hasExistingComprovante', false);
    setValue('isChangingComprovante', false);
    setValue('file', null);
    clearErrors('file');
  }, [clearErrors, setValue]);

  useEffect(() => {
    if (!responsavelTipo || !showModal) {
      setClientes([]);
      return;
    }

    // Só busca clientes se for paciente, nic_sp ou nic_go
    if (responsavelTipo !== 'paciente' && responsavelTipo !== 'nic_sp' && responsavelTipo !== 'nic_go') {
      setClientes([]);
      return;
    }

    (async () => {
      try {
        setLoadingClientes(true);
        let url = '/profissionalClientes';
        if (responsavelTipo === 'nic_sp' || responsavelTipo === 'nic_go') {
          url = `/profissionalClientes?responsavelTipo=${responsavelTipo}`;
        }

        const { data = [] } = await api.get(url);
        const clientesMapeados = data.map((d) => ({
          value: d._id,
          cpf: d.criancas.cpf,
          label: d.criancas.nome,
        }));
        setClientes(clientesMapeados);

        // Se for nic_sp ou nic_go, preencher automaticamente com o primeiro cliente
        if ((responsavelTipo === 'nic_sp' || responsavelTipo === 'nic_go')) {
          if (clientesMapeados.length > 0) {
            setValue('cliente', clientesMapeados[0]);
            clearErrors('cliente');
          } else {
            // Se não houver clientes, mostrar erro e notificação
            setValue('cliente', '');
            setError('cliente', {
              type: 'manual',
              message: 'Não foi encontrado cliente para o responsável escolhido'
            });
            notificationRef.current.notify({
              message: 'Não foi encontrado cliente para o responsável escolhido',
              color: 'warning'
            });
          }
        }

        setLoadingClientes(false);
      } catch (error) {
        setLoadingClientes(false);
        console.log(error);
        // Em caso de erro, também validar se for nic_sp ou nic_go
        if (responsavelTipo === 'nic_sp' || responsavelTipo === 'nic_go') {
          setValue('cliente', '');
          setError('cliente', {
            type: 'manual',
            message: 'Não foi encontrado cliente para o responsável escolhido'
          });
          notificationRef.current.notify({
            message: 'Não foi encontrado cliente para o responsável escolhido',
            color: 'warning'
          });
        }
      }
    })();
  }, [responsavelTipo, showModal, setValue, setError, clearErrors, notificationRef]);

  const handleSolicitarReembolso = useCallback(() => {
    setValue('responsavelTipo', 'paciente');
    setValue('cliente', '');
    setValue('formaPagamento', 'nicash');
    setValue('descricao', '');
    setValue('valor', '');
    setValue('dataCompra', '');
    setValue('localCompra', '');
    setValue('autorizadoPor', '');
    setValue('tipoDocumento', '');
    setValue('numeroDocumento', '');
    setValue('ultimosNumerosCartao', '');
    setValue('file', null);
    setValue('hasExistingComprovante', false);
    setValue('isChangingComprovante', false);
    clearErrors();
    setEditingId(null);
    setEditingComprovante(null);
    setShowFileInput(true);
    setShowModal(true);
  }, [setValue, clearErrors, setShowModal]);

  const handleDownloadComprovante = useCallback((comprovante) => {
    const filename = comprovante?.filename;
    const downloadName = comprovante?.name || filename;
    if (!filename) return;

    (async () => {
      try {
        setLoading({
          loading: true,
          message: 'Baixando arquivo...',
        });
        const res = await api.get(`/download/${filename}/files/profissional`, { responseType: 'blob' });
        const fileURL = window.URL.createObjectURL(res.data);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.target = '_blank';
        alink.rel = 'noopener noreferrer';
        alink.download = downloadName;
        alink.click();
        window.URL.revokeObjectURL(fileURL);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        notificationRef.current.notify({
          message: error?.response?.data?.message || 'Falha ao baixar comprovante',
          color: 'danger',
        });
      }
    })();
  }, [notificationRef, setLoading]);

  const handleEdit = useCallback((row) => {
    if (row.isBlocked) {
      alertModalRef.current.show({
        title: 'Aviso',
        message: row.message || 'Não é possível editar este reembolso no momento.',
        confirmText: 'Ok',
        confirmColor: 'warning',
        hideCancel: true,
        onConfirm: () => {
          alertModalRef.current.close();
        },
      });
      return;
    }

    setEditingId(row._id);
    setValue('responsavelTipo', row.responsavelTipo || 'paciente');
    setValue('cliente', row?.cliente ? { value: row.cliente?.id, cpf: row.cliente?.cpf, label: row.cliente?.nome } : '');
    setValue('formaPagamento', row.formaPagamento || 'nicash');
    setValue('descricao', toUpperCasePtBr(row.observacoes || row.descricao || ''));
    setValue('valor', row.valor || row.total || '');
    setValue('dataCompra', row.dataCompra ? row.dataCompra.substring(0, 10) : '');
    setValue('localCompra', toUpperCasePtBr(row.localCompra || ''));
    setValue('autorizadoPor', row.autorizadoPor || '');
    setValue('tipoDocumento', row.tipoDocumento ? { value: row.tipoDocumento, label: row.tipoDocumento } : '');
    setValue('numeroDocumento', row.numeroDocumento || '');
    setValue('ultimosNumerosCartao', row.ultimosNumerosCartao || '');
    setValue('file', null);
    const comprovante = row?.comprovante || null;
    const hasComprovante = !!comprovante?.filename;
    setEditingComprovante(comprovante);
    setShowFileInput(!hasComprovante);
    setValue('hasExistingComprovante', hasComprovante);
    setValue('isChangingComprovante', false);
    clearErrors();
    setShowModal(true);
  }, [clearErrors, setValue]);

  const handleDelete = useCallback((row) => {
    if (row.isBlocked) {
      alertModalRef.current.show({
        title: 'Aviso',
        message: row.message || 'Não é possível excluir este reembolso no momento.',
        confirmText: 'Ok',
        confirmColor: 'warning',
        hideCancel: true,
        onConfirm: () => {
          alertModalRef.current.close();
        },
      });
      return;
    }

    alertModalRef.current.show({
      title: 'Excluir Reembolso',
      message: 'Deseja realmente excluir este registro?',
      confirmText: 'Sim',
      confirmColor: 'danger',
      cancelText: 'Não',
      cancelColor: 'primary',
      onConfirm: async () => {
        alertModalRef.current.close();
        try {
          setLoading(true);
          await api.delete(`/profissionalReembolsos/${row._id}`);
          setReloadReembolsos((n) => n + 1);
          notificationRef.current.notify({ message: 'Excluído com sucesso' });
        } catch (err) {
          setLoading(false);
          if (err && err.response && err.response.data) {
            const { message } = err.response.data;

            if (message) {
              notificationRef.current.notify({
                message,
                color: 'danger',
              });

              return;
            }
          }

          notificationRef.current.notify({
            message: 'Falha ao excluir',
            color: 'danger',
          });
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => {
        alertModalRef.current.close();
      },
    });
  }, [notificationRef, setLoading]);

  const handleClonar = useCallback((row) => {
    if (row.isBlocked) {
      alertModalRef.current.show({
        title: 'Aviso',
        message: row.message || 'Não é possível clonar este reembolso no momento.',
        confirmText: 'Ok',
        confirmColor: 'warning',
        hideCancel: true,
        onConfirm: () => {
          alertModalRef.current.close();
        },
      });
      return;
    }

    const abrirFormularioClonado = () => {
      const fp = row.formaPagamento || 'nicash';
      setValue('formaPagamento', fp);
      setValue('responsavelTipo', row.responsavelTipo || 'paciente');
      setValue('cliente', row?.cliente
        ? { value: row.cliente?.id, cpf: row.cliente?.cpf, label: row.cliente?.nome }
        : '');
      setValue('descricao', '');
      setValue('valor', '');
      setValue('dataCompra', '');
      setValue('localCompra', '');
      setValue('autorizadoPor', row.autorizadoPor || '');
      setValue('tipoDocumento', '');
      setValue('numeroDocumento', '');
      setValue('ultimosNumerosCartao', fp !== 'reembolso' ? (row.ultimosNumerosCartao || '') : '');
      setValue('file', null);
      setValue('hasExistingComprovante', false);
      setValue('isChangingComprovante', false);
      clearErrors();
      setEditingId(null);
      setEditingComprovante(null);
      setShowFileInput(true);
      setShowModal(true);
    };

    alertModalRef.current.show({
      title: 'Clonagem de Reembolso',
      message:
        'Ao clonar o registro o novo reembolso irá herdar os campos de FORMA DE PAGAMENTO, RESPONSÁVEL PELO GASTO e AUTORIZADO POR do reembolso atual.\n\nDeseja continuar?',
      confirmText: 'Confirmar',
      confirmColor: 'success',
      cancelText: 'Cancelar',
      cancelColor: 'danger',
      onConfirm: () => {
        alertModalRef.current.close();
        abrirFormularioClonado();
      },
      onCancel: () => {
        alertModalRef.current.close();
      },
    });
  }, [clearErrors, setValue]);

  const onSubmitFinish = useCallback((formValues) => {
    console.log(formValues);
    const {
      cliente,
      responsavelTipo: formResponsavelTipo,
      formaPagamento: formaPagamentoSelecionada,
      descricao,
      valor,
      dataCompra,
      localCompra,
      autorizadoPor,
      tipoDocumento,
      numeroDocumento,
      ultimosNumerosCartao,
      file,
    } = formValues;

    if (formResponsavelTipo === 'paciente' && formaPagamentoSelecionada === 'nicash' && !cliente) {
      setError('cliente', { type: 'manual', message: 'Para essa forma de pagamento selecione um Paciente' });
      return;
    }

    (async () => {
      try {
        setLoading(true);
        const formData = new FormData();

        formData.append('cliente', cliente?.value ? JSON.stringify({
          id: cliente.value,
          cpf: cliente.cpf,
          nome: cliente.label,
        }) : '');
        formData.append('responsavelTipo', formResponsavelTipo);
        formData.append('formaPagamento', formaPagamentoSelecionada);
        formData.append('descricao', descricao);
        formData.append('valor', valor);
        formData.append('dataCompra', dataCompra || '');
        formData.append('localCompra', localCompra || '');
        formData.append('autorizadoPor', autorizadoPor || '');
        formData.append('tipoDocumento', tipoDocumento?.value ?? tipoDocumento ?? '');
        formData.append('numeroDocumento', numeroDocumento || '');
        formData.append('ultimosNumerosCartao', formaPagamentoSelecionada === 'reembolso' ? '' : (ultimosNumerosCartao || ''));

        if (file instanceof File) {
          formData.append('file', file);
        }

        let data;
        if (editingId) {
          const { data: resp } = await api.put(`/profissionalReembolsos/${editingId}`, formData);
          data = resp;
        } else {
          const resp = await api.post('/profissionalReembolsos', formData);
          data = resp.data;
        }
        setLoading(false);
        notificationRef.current.notify({
          message: editingId ? 'Reembolso atualizado com sucesso' : 'Solicitação de reembolso realizada com sucesso',
        });

        const wasEditing = !!editingId;
        handleCloseModal();
        if (!wasEditing) {
          setPage(1);
        }
        setReloadReembolsos((n) => n + 1);
      } catch (err) {
        setLoading(false);
        if (err && err.response && err.response.data) {
          const { message } = err.response.data;

          if (message) {
            notificationRef.current.notify({
              message,
              color: 'danger',
            });

            return;
          }
        }

        notificationRef.current.notify({
          message: 'Falha ao Realizar reembolso',
          color: 'danger',
        });
      }
    })();
  }, [editingId, handleCloseModal, notificationRef, setLoading]);

  const renderActions = useCallback((row) => (
    <ActionsCell row={row} onEdit={handleEdit} onDelete={handleDelete} onClonar={handleClonar} />
  ), [handleEdit, handleDelete, handleClonar]);

  const columns = useMemo(
    () => [
      {
        dataField: 'responsavelTipo',
        text: 'RESPONSÁVEL',
        formatter: (row) => (row.responsavelTipo === 'paciente' ? row.cliente?.nome : row.responsavelTipo === 'nic_sp' ? 'NIC São Paulo' : row.responsavelTipo === 'nic_go' ? 'NIC Goiania' : '-'),
      },
      {
        dataField: 'dataCompra',
        text: 'DATA DA COMPRA',
        formatter: (row) => new Date(row.dataCompra || row.createdAt).toLocaleDateString('pt-BR'),
      },
      {
        dataField: 'descricao',
        text: 'MOTIVO DO GASTO',
        formatter: (row) => row.descricao || '-',
      },
      {
        dataField: 'dataCompra',
        text: 'FORMA DE PAGAMENTO',
        formatter: (row) => row.formaPagamento === 'nicash' ? 'CARTÃO COORPORATIVO' : row.formaPagamento === 'reembolso' ? 'REEMBOLSO/CARTÃO PESSOAL' : '-',
      },
      {
        dataField: 'valor',
        text: 'VALOR',
        formatter: (row) => `R$ ${Number(row.valor || row.total || 0).toFixed(2)}`,
        width: '120px',
      },
      {
        dataField: 'actions',
        text: 'AÇÕES',
        formatter: renderActions,
        width: '280px',
      },
    ],
    [renderActions],
  );

  return (
    <Container fluid>
      <AlertModal ref={alertModalRef} />
      <Row className="justify-content-center">
        <Col>
          <div className="d-flex flex-column justify-content-center">
            <h3 className="text-center font-weight-700">Reembolsos</h3>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={6} md={4} className="ml-auto">
          <Button color="success" onClick={handleSolicitarReembolso} block>
            Informar nova compra
          </Button>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <CustomTable
            columns={columns}
            data={reembolsos}
            loading={loadingReembolsos}
            page={page}
            limit={limit}
            totalDocs={totalDocs}
            totalPages={totalPages}
            hasPrevPage={hasPrevPage}
            hasNextPage={hasNextPage}
            onChangePage={setPage}
          />
        </Col>
      </Row>
      <Modal
        isOpen={showModal}
        toggle={handleCloseModal}
        centered
      >
        <Form onSubmit={handleSubmit(onSubmitFinish)}>
          <ModalHeader>
            <strong>{editingId ? 'Atualizar reembolso' : 'Solicitar reembolso'}</strong>
            <Button
              className="close position-absolute top-4 right-4 py-1 px-2"
              onClick={handleCloseModal}
            >
              <i className="fas fa-times" />
            </Button>
          </ModalHeader>
          <ModalBody>
            <Controller
              name="formaPagamento"
              control={control}
              render={({ field }) => (
                <FormGroup>
                  <Label>Forma de Pagamento (*)</Label>
                  <div className="d-flex flex-column">
                    <FormGroup check className="mb-2">
                      <Label check>
                        <Input
                          type="radio"
                          name="formaPagamento"
                          checked={field.value === 'nicash'}
                          onChange={() => field.onChange('nicash')}
                        />
                        CARTÃO COORPORATIVO
                      </Label>
                    </FormGroup>
                    <FormGroup check>
                      <Label check>
                        <Input
                          type="radio"
                          name="formaPagamento"
                          checked={field.value === 'reembolso'}
                          onChange={() => field.onChange('reembolso')}
                        />
                        REEMBOLSO/CARTÃO PESSOAL
                      </Label>
                    </FormGroup>
                  </div>
                  <FormFeedback style={{ display: errors?.formaPagamento?.message ? 'block' : 'none' }}>{errors?.formaPagamento?.message}</FormFeedback>
                </FormGroup>
              )}
            />
            <Controller
              name="responsavelTipo"
              control={control}
              render={({ field }) => (
                <FormGroup>
                  <Label>Responsável pelo gasto (*)</Label>
                  <div className="d-flex flex-column">
                    <FormGroup check className="mb-2">
                      <Label check>
                        <Input
                          type="radio"
                          name="responsavelTipo"
                          checked={field.value === 'nic_sp'}
                          onChange={() => field.onChange('nic_sp')}
                        />
                        NIC São Paulo
                      </Label>
                    </FormGroup>
                    <FormGroup check className="mb-2">
                      <Label check>
                        <Input
                          type="radio"
                          name="responsavelTipo"
                          checked={field.value === 'nic_go'}
                          onChange={() => field.onChange('nic_go')}
                        />
                        NIC Goiania
                      </Label>
                    </FormGroup>
                    <FormGroup check>
                      <Label check>
                        <Input
                          type="radio"
                          name="responsavelTipo"
                          checked={field.value === 'paciente'}
                          onChange={() => field.onChange('paciente')}
                        />
                        Paciente/Cliente/Família
                      </Label>
                    </FormGroup>
                  </div>
                  <FormFeedback style={{ display: errors?.responsavelTipo?.message ? 'block' : 'none' }}>{errors?.responsavelTipo?.message}</FormFeedback>
                </FormGroup>
              )}
            />
            <Controller
              name="cliente"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  label="Responsável pelo gasto"
                  placeholder="Selecione o responsável pelo gasto"
                  loading={loadingClientes}
                  loadingMessage={() => 'Carregando pacientes...'}
                  noOptionsMessage="Nenhum paciente encontrado"
                  invalidMessage={errors?.cliente?.message}
                  isDisabled={responsavelTipo === 'nic_sp' || responsavelTipo === 'nic_go'}
                  {...field}
                  options={clientes}
                />
              )}
            />
            <Controller
              name="descricao"
              control={control}
              render={({ field }) => (
                <FormGroup>
                  <Label>Descrição do item Comprado</Label>
                  <Input
                    type="textarea"
                    placeholder="Descreva o item comprado"
                    value={field.value}
                    maxLength={40}
                    invalid={!!errors?.descricao?.message}
                    onChange={(e) => field.onChange(toUpperCasePtBr(e.target.value))}
                  />
                  <div className="d-flex justify-content-end">
                    <small className="text-muted">
                      {(field.value || '').length}
                      /40
                    </small>
                  </div>
                  <FormFeedback>{errors?.descricao?.message}</FormFeedback>
                </FormGroup>
              )}
            />
            <Controller
              name="valor"
              control={control}
              render={({ field }) => (
                <MoneyMaskedInput
                  label="Valor da Compra"
                  invalidMessage={errors?.valor?.message}
                  value={field.value}
                  onValueChange={({ floatValue }) => field.onChange(floatValue)}
                />
              )}
            />
            <Controller
              name="dataCompra"
              control={control}
              render={({ field }) => (
                <FormGroup>
                  <Label>Data da Compra</Label>
                  <Input
                    type="date"
                    placeholder="Informe a data da compra"
                    value={field.value}
                    invalid={!!errors?.dataCompra?.message}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                  <FormFeedback>{errors?.dataCompra?.message}</FormFeedback>
                </FormGroup>
              )}
            />
            <Controller
              name="localCompra"
              control={control}
              render={({ field }) => (
                <FormGroup>
                  <Label>Local da Compra</Label>
                  <Input
                    type="text"
                    placeholder="Informe o nome do estabelecimento"
                    value={field.value}
                    maxLength={40}
                    invalid={!!errors?.localCompra?.message}
                    onChange={(e) => field.onChange(toUpperCasePtBr(e.target.value))}
                  />
                  <div className="d-flex justify-content-end">
                    <small className="text-muted">
                      {(field.value || '').length}
                      /40
                    </small>
                  </div>
                  <FormFeedback>{errors?.localCompra?.message}</FormFeedback>
                </FormGroup>
              )}
            />
            <Controller
              name="autorizadoPor"
              control={control}
              render={({ field }) => (
                <FormGroup>
                  <Label>Autorizado por</Label>
                  <Input
                    type="text"
                    placeholder="Informe o nome do Pai, Mãe, Ana, Luana, Mariana, Nome do supervisor(a) do caso"
                    value={field.value}
                    maxLength={20}
                    invalid={!!errors?.autorizadoPor?.message}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                  <div className="d-flex justify-content-end">
                    <small className="text-muted">
                      {(field.value || '').length}
                      /20
                    </small>
                  </div>
                  <FormFeedback>{errors?.autorizadoPor?.message}</FormFeedback>
                </FormGroup>
              )}
            />
            <Controller
              name="tipoDocumento"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  label="TIPO DE DOCUMENTO (*)"
                  placeholder="Selecione o tipo de documento"
                  invalidMessage={errors?.tipoDocumento?.message}
                  {...field}
                  options={[
                    { value: 'NOTA FISCAL', label: 'NOTA FISCAL' },
                    { value: 'CUPOM FISCAL', label: 'CUPOM FISCAL' },
                    { value: 'RECIBO', label: 'RECIBO' },
                  ]}
                />
              )}
            />
            <Controller
              name="numeroDocumento"
              control={control}
              render={({ field }) => (
                <FormGroup>
                  <Label>Número do documento (*)</Label>
                  <Input
                    type="text"
                    placeholder="Informe o número do documento"
                    value={field.value}
                    maxLength={15}
                    invalid={!!errors?.numeroDocumento?.message}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                  <div className="d-flex justify-content-end">
                    <small className="text-muted">
                      {(field.value || '').length}
                      /15
                    </small>
                  </div>
                  <FormFeedback>{errors?.numeroDocumento?.message}</FormFeedback>
                </FormGroup>
              )}
            />
            <Controller
              name="ultimosNumerosCartao"
              control={control}
              render={({ field }) => {
                if (formaPagamento === 'reembolso') {
                  return null;
                }

                return (
                  <FormGroup>
                    <Label>4º ÚLTIMOS NUMEROS DO CARTÃO UTILIZADO (*)</Label>
                    <Input
                      type="text"
                      placeholder="Informe os últimos números do cartão"
                      value={field.value}
                      maxLength={6}
                      inputMode="numeric"
                      invalid={!!errors?.ultimosNumerosCartao?.message}
                      onChange={(e) => {
                        const v = (e.target.value || '').replace(/\D/g, '').slice(0, 6);
                        field.onChange(v);
                      }}
                    />
                    <div className="d-flex justify-content-end">
                      <small className="text-muted">
                        {(field.value || '').length}
                        /6
                      </small>
                    </div>
                    <FormFeedback>{errors?.ultimosNumerosCartao?.message}</FormFeedback>
                  </FormGroup>
                );
              }}
            />
            <Controller
              name="file"
              control={control}
              render={({ field }) => (
                <FormGroup>
                  <Label for="comprovante">{showFileInput ? 'Enviar Comprovante (*)' : 'Comprovante'}</Label>
                  {!showFileInput && editingComprovante?.filename ? (
                    <div className="d-flex">
                      <Button
                        color="info"
                        size="sm"
                        className="mr-2"
                        type="button"
                        onClick={() => handleDownloadComprovante(editingComprovante)}
                      >
                        <i className="fa-solid fa-download mr-1" />
                        Visualizar
                      </Button>
                      <Button
                        color="warning"
                        size="sm"
                        type="button"
                        onClick={() => {
                          setShowFileInput(true);
                          setValue('isChangingComprovante', true);
                        }}
                      >
                        Alterar comprovante
                      </Button>
                    </div>
                  ) : (
                    <Input
                      id="comprovante"
                      type="file"
                      placeholder="Comprovante"
                      accept="image/*"
                      invalid={!!errors?.file?.message}
                      onChange={({ target: { files } }) => field.onChange(files[0])}
                    />
                  )}
                  <FormFeedback style={{ display: errors?.file?.message ? 'block' : 'none' }}>{errors?.file?.message}</FormFeedback>
                </FormGroup>
              )}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="success" type="submit">{editingId ? 'Atualizar' : 'Solicitar'}</Button>
          </ModalFooter>
        </Form>
      </Modal>
    </Container>
  );
}

export default Reembolsos;
